import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import React, { useMemo } from 'react';
import { Card, Col, Row, Table } from 'react-bootstrap';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { getColor, formatMoney } from 'helpers/utils';
import * as echarts from 'echarts/core';
import { BarChart, PieChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import FalconCardHeader from 'components/common/FalconCardHeader';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useDonorInsights } from 'hooks/useDonors';

// Register the required components
echarts.use([
  BarChart,
  PieChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  CanvasRenderer
]);

const getCategoryPieOptions = categories => ({
  color: [
    getColor('primary'),
    getColor('success'),
    getColor('info'),
    getColor('danger'),
    getColor('warning'),
    getColor('secondary')
  ],
  tooltip: {
    trigger: 'item',
    padding: [7, 10],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    textStyle: { color: getColor('dark') },
    borderWidth: 1,
    transitionDuration: 0,
    formatter: params =>
      `<strong>${params.name}:</strong> ${formatMoney(
        params.data.actualAmount
      )} (${params.value}%)`
  },
  legend: {
    show: true,
    orient: 'vertical',
    left: 'left',
    top: 'center',
    textStyle: {
      fontSize: 12,
      color: getColor('gray-700')
    }
  },
  series: [
    {
      type: 'pie',
      radius: ['40%', '70%'],
      center: ['60%', '50%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderWidth: 2,
        borderColor: getColor('gray-100')
      },
      label: {
        show: true,
        position: 'outer',
        formatter: '{d}%',
        fontSize: 11
      },
      emphasis: {
        label: {
          show: true,
          fontSize: 12,
          fontWeight: 'bold'
        }
      },
      data: categories.map(item => ({
        name: item.name,
        value: parseFloat(item.percentage),
        actualAmount: item.amount
      }))
    }
  ]
});

export const DonationData = ({ data, isLastItem }) => (
  <Flex className={!isLastItem ? 'border-md-end me-md-4 me-lg-5' : 'border-0'}>
    <FontAwesomeIcon
      icon={'circle'}
      className={classNames('me-2 mt-1', data.statusColor)}
      size="xs"
    />
    <div className="fw-medium">
      <p className="mb-0 text-600" style={{ fontSize: '1rem' }}>{data.title}</p>
      {data.amount ? (
        <p className="mb-0 fs-2" style={{ fontSize: '1rem' }}>
          {data.amount}{' '}
          {data.notification ? (
            <span
              className="border d-inline-flex fs--1 ms-1 justify-content-center rounded-circle"
              style={{ height: 20, width: 20 }}
            >
              {data.notification}
            </span>
          ) : null}
        </p>
      ) : null}

      {data.info && (
        <p className={classNames('mb-0', data.statusColor)} style={{ fontSize: '1rem' }}>{data.info}</p>
      )}

      {!data.donationCount ? (
        <div>
          {data.lastDonation ? (
            <>
              <p className="mb-0 text-500" style={{ fontSize: '1rem' }}>last Donation</p>
              <p className="mb-0" style={{ fontSize: '1rem' }}>{data.lastDonation}</p>
            </>
          ) : (
            <p style={{ fontSize: '1rem' }}>No Period Comparison</p>
          )}
        </div>
      ) : (
        <p className="mb-0 text-500" style={{ fontSize: '1rem' }}>
          <span className="text-900">{data.donationCount}</span>{' '}
          {data.donationCount === 1 ? 'donation' : 'donations'} per year (avg)
        </p>
      )}
    </div>
  </Flex>
);

const ProfileInsights = () => {
  const { id: donorId } = useParams();
  const { insights, isLoading } = useDonorInsights(donorId);

  // Get last 3 years for chart
  const currentYear = dayjs().year();
  const years = [currentYear - 2, currentYear - 1, currentYear];

  const yarns = years;
  const data = [
    insights?.yearlyDonations?.[years[0]] || 0,
    insights?.yearlyDonations?.[years[1]] || 0,
    insights?.yearlyDonations?.[years[2]] || 0
  ];

  const getOption = () => ({
    tooltip: {
      trigger: 'axis',
      padding: [7, 10],
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('dark') },
      borderWidth: 1,
      transitionDuration: 0,
      axisPointer: {
        type: 'none'
      }
    },
    xAxis: {
      type: 'category',
      data: yarns,
      axisLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'solid'
        }
      },
      axisTick: { show: false },
      axisLabel: {
        color: getColor('gray-800'),
        formatter: value => value.substring(0, 4),
        margin: 15
      },
      splitLine: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        show: true,
        color: getColor('gray-800'),
        margin: 15
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: getColor('gray-300'),
          type: 'dashed'
        }
      },
      axisTick: { show: false },
      axisLine: { show: false },
      min: 0,
      minInterval: 500
    },
    series: [
      {
        type: 'bar',
        name: 'Total',
        data,
        lineStyle: { color: getColor('primary') },
        itemStyle: {
          color: getColor('primary'),
          borderRadius: [3, 3, 0, 0]
        },
        barWidth: 13,
        showSymbol: false,
        symbol: 'circle',
        smooth: false,
        emphasis: {
          scale: true
        }
      }
    ],
    grid: { right: '3%', left: '20%', bottom: '12%', top: '5%' }
  });

  const donorLifeTimeContribution = insights?.lifetimeContribution || 0;
  const donorYearToDayContribution = insights?.ytdContribution || 0;
  const donorFirstDonationAmount = insights?.firstDonation || 0;
  const donorMostRecentDonationAmount = insights?.mostRecentDonation || 0;
  const donorCreationDate = insights?.donorCreatedAt || '';
  const donorAverageDonationAmounts = insights?.averageDonation || 0;
  const lastDonationDate = insights?.lastDonationDate || '';

  // Calculate donation frequency
  const calculateFrequency = () => {
    if (!insights?.totalDonations || insights.totalDonations < 2) {
      return { frequency: 'New Donor', averagePerYear: 0 };
    }

    const totalDonations = insights.totalDonations;
    const donorCreated = new Date(insights.donorCreatedAt);
    const lastDonation = new Date(insights.lastDonationDate || new Date());
    const yearsDiff = Math.max(
      1,
      (lastDonation - donorCreated) / (365 * 24 * 60 * 60 * 1000)
    );
    const averagePerYear = Math.round(totalDonations / yearsDiff);

    let frequency = 'Low';
    if (averagePerYear >= 12) frequency = 'Very High';
    else if (averagePerYear >= 6) frequency = 'High';
    else if (averagePerYear >= 3) frequency = 'Moderate';
    else if (averagePerYear >= 1) frequency = 'Low';

    return { frequency, averagePerYear };
  };

  const { frequency, averagePerYear } = calculateFrequency();

  // Process donor-specific giving categories
  const processDonorCategories = () => {
    if (!insights?.categories || Object.keys(insights.categories).length === 0) {
      return [];
    }

    return Object.entries(insights.categories)
      .map(([name, data]) => ({
        name,
        amount: data.amount,
        percentage: data.percentage
      }))
      .sort((a, b) => b.amount - a.amount);
  };

  const donorCategories = processDonorCategories();

  const profileDetails = [
    {
      id: 1,
      title: 'LifeTime Contribution',
      value: formatMoney(donorLifeTimeContribution)
    },
    {
      id: 2,
      title: 'YTD Contribution',
      value: formatMoney(donorYearToDayContribution)
    },
    { id: 3, title: 'Outstanding Pledges', value: formatMoney(0) },
    {
      id: 4,
      title: 'First Donation',
      value: formatMoney(donorFirstDonationAmount)
    },
    {
      id: 5,
      title: 'Most recent Donation',
      value: formatMoney(donorMostRecentDonationAmount)
    },
    {
      id: 6,
      title: 'Created',
      value: dayjs(donorCreationDate).format('DD-MMM-YYYY'),
      valueClassName: 'text-400'
    }
    // { id: 7, title: 'Created By', value: 'Ahmed Adel' },
    // {
    //   id: 8,
    //   title: 'Current Assignee',
    //   value: 'Ali Mohamed',
    //   valueClassName: 'text-primary',
    //   link: '/'
    // }
  ];

  const donationInfo = [
    {
      id: 1,
      statusColor: 'text-primary',
      title: 'Average Donations',
      amount: formatMoney(donorAverageDonationAmounts),
      notification: 0,
      lastDonation: lastDonationDate
        ? dayjs(lastDonationDate).format('DD-MMM-YYYY')
        : ''
    },
    {
      id: 2,
      statusColor: 'text-warning',
      title: 'Donated to date',
      amount: formatMoney(donorLifeTimeContribution),
      notification: 0,
      lastDonation: ''
    },
    {
      id: 3,
      statusColor: 'text-success',
      title: 'Frequency',
      amount: '',
      info: frequency,
      notification: 0,
      donationCount: averagePerYear
    }
  ];

  return (
    <>
      <Card className="mb-3">
        <FalconCardHeader
          title={<h4 className="fw-medium" style={{ fontSize: '1.25rem' }}>Profile Overview</h4>}
          light={false}
          titleTag="div"
          className="pb-1"
          menuClassName="align-items-start"
          // endEl={<CardDropdown btnRevealClass="btn-falcon-default" />}
        />
        <Card.Body>
          <Row>
            <Col md={6}>
              <Table size="sm" borderless className="font-sans-serif mb-0">
                <tbody>
                  {profileDetails?.map(detail => (
                    <tr key={detail.id}>
                      <td className="text-start ps-0 text-900" style={{ fontSize: '1rem' }}>{detail.title}</td>
                      <td
                        className={classNames(
                          'text-start',
                          detail.valueClassName
                        )}
                      >
                        {detail.link ? (
                          <a href={detail.link} style={{ fontSize: '1rem' }}>{detail.value}</a>
                        ) : (
                          <span style={{ fontSize: '1rem' }}>{detail.value}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
            <Col md={1} />
            <Col md={5}>
              <div>
                <ReactEChartsCore
                  echarts={echarts}
                  option={getOption()}
                  style={{ height: '14.75rem' }}
                />
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3 ">
        <Card.Header>
          <Card.Title className="fw-medium" style={{ fontSize: '1.25rem' }}>Insights</Card.Title>
        </Card.Header>
        <Card.Body className="pt-0">
          <Row className="g-3">
            {donationInfo?.map((info, index) => (
              <Col key={info.id} md={4}>
                <DonationData
                  data={info}
                  isLastItem={index === donationInfo.length - 1}
                />
              </Col>
            ))}
          </Row>
          <hr />
          <Row className="g-3">
            <Col md={12}>
              <p className="mb-0 fw-medium" style={{ fontSize: '1rem' }}>
                <FontAwesomeIcon icon={'user'} className="me-1" />
                Giving Categories Snapshot
              </p>

              {donorCategories.length > 0 ? (
                <Row className="g-3 mt-0">
                  <Col md={12}>
                    <div className="border-top pt-3">
                      <ReactEChartsCore
                        echarts={echarts}
                        option={getCategoryPieOptions(donorCategories)}
                        style={{ height: '300px' }}
                      />
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="border-top pt-3">
                      <h6 className="text-800 mb-3" style={{ fontSize: '1rem' }}>Category Breakdown</h6>
                      <Row className="g-2">
                        {donorCategories.map(item => (
                          <Col md={6} key={item.name}>
                            <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                              <span className="text-700" style={{ fontSize: '1rem' }}>{item.name}</span>
                              <span className="fw-semi-bold" style={{ fontSize: '1rem' }}>
                                {formatMoney(item.amount)}
                                <span className="text-500 ms-1" style={{ fontSize: '0.875rem' }}>
                                  ({item.percentage}%)
                                </span>
                              </span>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </Col>
                </Row>
              ) : (
                <Row className="g-3 mt-0">
                  <Col md={12}>
                    <div className="border-top pt-3 text-center text-muted" style={{ fontSize: '1rem' }}>
                      No donation categories found
                    </div>
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
          <hr />
          {/* <Row>
            <Col md={12}>
              <Flex className={'mb-3'}>
                <div
                  style={{ height: 32, width: 32 }}
                  className="border rounded-circle d-flex align-items-center justify-content-center bg-soft-success"
                >
                  <FontAwesomeIcon icon={'trophy'} className="text-success" />
                </div>
                <div className="ms-3">
                  <p className="fw-semi-bold mb-0 text-800">
                    Donor Score and status
                  </p>
                  <p className="fw-semi-bold mb-0 text-1000">
                    Active Donor,{' '}
                    <span className="fw-medium text-800">
                      Among the top 32% within your orgniazation
                    </span>
                  </p>
                </div>
              </Flex>
              <Flex>
                <div
                  style={{ height: 32, width: 32 }}
                  className="border rounded-circle d-flex align-items-center justify-content-center bg-soft-primary"
                >
                  <FontAwesomeIcon icon={'envelope'} className="text-primary" />
                </div>
                <div className="ms-3">
                  <p className="fw-semi-bold mb-0 text-800">Smart Ask</p>
                  <p className="fw-medium mb-0">
                    This donor is most likely to donate between{' '}
                    <span className="fw-semi-bold text-1000">
                      $5,500 to $8,000.
                    </span>
                    We recommend asking for{' '}
                    <span className="fw-semi-bold text-primary">$7,000</span>
                  </p>
                </div>
              </Flex>
            </Col>
          </Row> */}
        </Card.Body>
      </Card>

      {/* <Card className="h-lg-100">
        <Card.Body>
          <Row className="g-3 align-items-center">
            <Col xs={12} sm={6} xl={4} className="pe-xl-0">
              <h2 className="text-primary fw-semi-bold">{formatMoney(5500)}</h2>
              <p className="mb-0">to {formatMoney(8000)}.</p>
            </Col>
            <Col xs={12} sm={6} xl={8} className="ps-xl-0">
              <p className="fs--1 mb-0 mt-3 mt-sm-0">
                <span className="fw-semi-bold text-primary">
                  {formatMoney(7000)}
                </span>{' '}
                will be donated by you in the next 6 years (estimated).
              </p>
            </Col>
          </Row>
        </Card.Body>
      </Card> */}
    </>
  );
};

DonationData.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    statusColor: PropTypes.string,
    info: PropTypes.string,
    lastDonation: PropTypes.string,
    notification: PropTypes.number,
    donationCount: PropTypes.number
  }),

  isLastItem: PropTypes.bool
};

export default ProfileInsights;
